/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
"use client";

import { usePathname, useRouter } from "next/navigation";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

type NavigateOptions = {
	origin?: {
		x: number;
		y: number;
	};
	replace?: boolean;
};

type PageTransitionContextValue = {
	isTransitioning: boolean;
	navigate: (href: string, options?: NavigateOptions) => Promise<void>;
};

type TransitionMode = "anchor" | "route";
type TransitionTrigger = "back" | "barba" | "forward" | "popstate";

type TransitionData = {
	current: {
		namespace: string;
		url: {
			hash: string;
			href: string;
			path: string;
			query: Record<string, string>;
		};
	};
	next: {
		namespace: string;
		url: {
			hash: string;
			href: string;
			path: string;
			query: Record<string, string>;
		};
	};
	trigger: TransitionTrigger;
};

type PendingNavigation = {
	origin: {
		x: number;
		y: number;
	};
	hash: string;
	mode: TransitionMode;
	routeKey: string;
	trigger: TransitionTrigger;
};

type BarbaInstance = {
	init: (options: {
		debug?: boolean;
		prevent?: () => boolean;
		preventRunning?: boolean;
	}) => void;
	destroy: () => void;
	hooks: BarbaHooks;
	transitions: {
		isRunning?: boolean;
	};
};

type BarbaHooks = {
	do: (
		hook: string,
		data?: TransitionData,
		transition?: { name: string },
	) => Promise<void>;
};

const BARBA_TRANSITION = { name: "next-app-router-transition" };

let gsapPromise: Promise<typeof import("gsap")["default"]> | null = null;
let barbaPromise: Promise<BarbaInstance> | null = null;

function loadGsap() {
	if (!gsapPromise) {
		gsapPromise = import("gsap").then((module) => module.default);
	}

	return gsapPromise;
}

function loadBarba() {
	if (!barbaPromise) {
		barbaPromise = import("@barba/core").then(
			(module) => module.default as BarbaInstance,
		);
	}

	return barbaPromise;
}

const PageTransitionContext = createContext<PageTransitionContextValue | null>(
	null,
);

function buildRouteKey(url: URL) {
	return `${url.pathname}${url.search}`;
}

function buildNamespace(url: URL) {
	const value = buildRouteKey(url).replaceAll(/[^a-zA-Z0-9]+/g, "-");
	return value.length > 0 ? value : "home";
}

function buildQuery(url: URL) {
	return Object.fromEntries(url.searchParams.entries());
}

function createTransitionData(
	currentUrl: URL,
	nextUrl: URL,
	trigger: TransitionTrigger,
): TransitionData {
	return {
		current: {
			namespace: buildNamespace(currentUrl),
			url: {
				hash: currentUrl.hash,
				href: currentUrl.href,
				path: currentUrl.pathname,
				query: buildQuery(currentUrl),
			},
		},
		next: {
			namespace: buildNamespace(nextUrl),
			url: {
				hash: nextUrl.hash,
				href: nextUrl.href,
				path: nextUrl.pathname,
				query: buildQuery(nextUrl),
			},
		},
		trigger,
	};
}

function getHeaderOffset() {
	const header = document.querySelector("header");
	return header instanceof HTMLElement ? header.offsetHeight : 0;
}

async function waitForNextFrame() {
	await new Promise<void>((resolve) => {
		requestAnimationFrame(() => resolve());
	});
}

async function scrollToHashTarget(hash: string) {
	if (!hash) {
		window.scrollTo({ top: 0, behavior: "auto" });
		return;
	}

	const targetId = decodeURIComponent(hash.replace(/^#/, ""));

	for (let attempt = 0; attempt < 8; attempt += 1) {
		const target = document.getElementById(targetId);

		if (target) {
			const top = Math.max(
				target.getBoundingClientRect().top + window.scrollY - getHeaderOffset(),
				0,
			);

			window.scrollTo({ top, behavior: "auto" });
			return;
		}

		await waitForNextFrame();
	}
}

function setScrollLock(locked: boolean) {
	document.documentElement.style.overflow = locked ? "hidden" : "";
	document.body.style.overflow = locked ? "hidden" : "";
}

function getViewportOrigin() {
	return {
		x: window.innerWidth / 2,
		y: window.innerHeight / 2,
	};
}

export function PageTransitionProvider({ children }: { children: ReactNode }) {
	const router = useRouter();
	const pathname = usePathname();
	const [search, setSearch] = useState("");

	useEffect(() => {
		setSearch(window.location.search);
	}, [pathname]);

	const routeKey = useMemo(() => `${pathname}${search}`, [pathname, search]);

	const [isTransitioning, setIsTransitioning] = useState(false);

	const wrapperRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const overlayRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const barbaRef = useRef<BarbaInstance | null>(null);
	const hooksRef = useRef<BarbaHooks | null>(null);

	const pendingNavigationRef = useRef<PendingNavigation | null>(null);
	const completionResolverRef = useRef<(() => void) | null>(null);
	const latestHrefRef = useRef<string>("");
	const reducedMotionRef = useRef(false);

	useEffect(() => {
		latestHrefRef.current = `${window.location.origin}${routeKey}${window.location.hash}`;
	}, [routeKey]);

	useEffect(() => {
		reducedMotionRef.current = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		if (!wrapperRef.current || !contentRef.current) {
			return;
		}

		let cancelled = false;

		async function setupBarba() {
			await loadGsap();
			const barba = await loadBarba();

			if (cancelled) return;

			barbaRef.current = barba;
			hooksRef.current = barba.hooks;

			barba.init({
				debug: false,
				prevent: () => true,
				preventRunning: true,
			});
		}

		type WindowWithIdle = Window & {
			requestIdleCallback?: (cb: () => void) => number;
		};

		const w = window as WindowWithIdle;

		const preload = w.requestIdleCallback
			? w.requestIdleCallback(() => {
					void setupBarba();
				})
			: window.setTimeout(() => {
					void setupBarba();
				}, 1);

		return () => {
			cancelled = true;
			if ("cancelIdleCallback" in window && typeof preload === "number") {
				window.cancelIdleCallback(preload);
			} else {
				window.clearTimeout(preload);
			}
			barbaRef.current?.destroy();
			barbaRef.current = null;
			hooksRef.current = null;
			setScrollLock(false);
		};
	}, []);

	const runHooks = useCallback(
		async (hook: string, data: TransitionData): Promise<void> => {
			if (!hooksRef.current?.do) return;

			await hooksRef.current.do(hook, data, BARBA_TRANSITION);
		},
		[],
	);

	const setBarbaRunning = useCallback((isRunning: boolean) => {
		if (!barbaRef.current?.transitions) return;

		barbaRef.current.transitions.isRunning = isRunning;
	}, []);

	const getBarbaIsRunning = useCallback(() => {
		return Boolean(barbaRef.current?.transitions?.isRunning);
	}, []);

	const animateLeave = useCallback(async (mode: TransitionMode) => {
		const overlay = overlayRef.current;
		const panel = panelRef.current;
		const content = contentRef.current;
		const pendingNavigation = pendingNavigationRef.current;

		if (!overlay || !panel || !content || !pendingNavigation) {
			return;
		}

		const gsap = await loadGsap();

		gsap.killTweensOf([overlay, panel, content]);

		if (reducedMotionRef.current) {
			await gsap.to(overlay, {
				autoAlpha: 1,
				backgroundColor: "#141414",
				duration: 0.18,
				ease: "power1.out",
			});

			return;
		}

		const timeline = gsap.timeline();

		timeline
			.set(overlay, {
				autoAlpha: 1,
			})
			.set(panel, {
				scaleX: 0,
				transformOrigin: "right center",
			})
			.to(
				panel,
				{
					duration: mode === "anchor" ? 0.42 : 0.56,
					ease: "power4.inOut",
					scaleX: 1,
				},
				0,
			)
			.to(
				content,
				{
					duration: mode === "anchor" ? 0.22 : 0.3,
					ease: "power3.out",
					opacity: 0.82,
				},
				0,
			);

		await timeline.then();
	}, []);

	const animateEnter = useCallback(async (mode: TransitionMode) => {
		const overlay = overlayRef.current;
		const panel = panelRef.current;
		const content = contentRef.current;
		const pendingNavigation = pendingNavigationRef.current;

		if (!overlay || !panel || !content || !pendingNavigation) {
			return;
		}

		const gsap = await loadGsap();

		gsap.killTweensOf([overlay, panel, content]);

		if (reducedMotionRef.current) {
			gsap.set(content, { clearProps: "all" });

			await gsap.to(overlay, {
				autoAlpha: 0,
				duration: 0.16,
				ease: "power1.out",
			});

			return;
		}

		const timeline = gsap.timeline();

		timeline
			.set(content, {
				opacity: 0,
			})
			.set(panel, {
				scaleX: 1,
				transformOrigin: "left center",
			})
			.to(
				content,
				{
					duration: mode === "anchor" ? 0.3 : 0.38,
					ease: "power3.out",
					opacity: 1,
				},
				0.08,
			)
			.to(
				panel,
				{
					duration: mode === "anchor" ? 0.42 : 0.56,
					ease: "power4.inOut",
					scaleX: 0,
				},
				0,
			)
			.set(overlay, {
				autoAlpha: 0,
			})
			.set(content, {
				clearProps: "all",
			});

		await timeline.then();
	}, []);

	const runLeave = useCallback(
		async (
			currentUrl: URL,
			nextUrl: URL,
			mode: TransitionMode,
			trigger: TransitionTrigger,
		) => {
			const data = createTransitionData(currentUrl, nextUrl, trigger);

			setBarbaRunning(true);
			setIsTransitioning(true);
			setScrollLock(true);

			await runHooks("before", data);
			await runHooks("beforeLeave", data);
			await runHooks("leave", data);
			await animateLeave(mode);
			await runHooks("afterLeave", data);
		},
		[animateLeave, runHooks, setBarbaRunning],
	);

	const runEnter = useCallback(
		async (
			currentUrl: URL,
			nextUrl: URL,
			mode: TransitionMode,
			trigger: TransitionTrigger,
		) => {
			const data = createTransitionData(currentUrl, nextUrl, trigger);

			await runHooks("beforeEnter", data);
			await runHooks("enter", data);
			await animateEnter(mode);
			await runHooks("afterEnter", data);
			await runHooks("after", data);

			setBarbaRunning(false);
			setIsTransitioning(false);
			setScrollLock(false);
		},
		[animateEnter, runHooks, setBarbaRunning],
	);

	const navigate = useCallback(
		async (href: string, options?: NavigateOptions) => {
			if (getBarbaIsRunning() || pendingNavigationRef.current) {
				return;
			}

			const currentUrl = new URL(window.location.href);
			const nextUrl = new URL(href, currentUrl.href);

			if (nextUrl.origin !== currentUrl.origin) {
				window.location.assign(nextUrl.href);
				return;
			}

			const currentRouteKey = buildRouteKey(currentUrl);
			const nextRouteKey = buildRouteKey(nextUrl);
			const sameRoute = currentRouteKey === nextRouteKey;
			const sameHash = currentUrl.hash === nextUrl.hash;

			if (sameRoute && sameHash) {
				return;
			}

			if (sameRoute) {
				const method = options?.replace ? "replaceState" : "pushState";

				window.history[method](window.history.state, "", nextUrl.href);

				await waitForNextFrame();
				await scrollToHashTarget(nextUrl.hash);

				latestHrefRef.current = window.location.href;

				return;
			}

			const mode: TransitionMode = "route";
			const trigger: TransitionTrigger = "barba";

			pendingNavigationRef.current = {
				origin: options?.origin ?? getViewportOrigin(),
				hash: nextUrl.hash,
				mode,
				routeKey: nextRouteKey,
				trigger,
			};

			await runLeave(currentUrl, nextUrl, mode, trigger);

			const targetHref = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

			await new Promise<void>((resolve) => {
				completionResolverRef.current = resolve;

				if (options?.replace) {
					router.replace(targetHref, { scroll: false });
					return;
				}

				router.push(targetHref, { scroll: false });
			});
		},
		[getBarbaIsRunning, router, runLeave],
	);

	useEffect(() => {
		const pendingNavigation = pendingNavigationRef.current;

		if (!pendingNavigation || pendingNavigation.mode !== "route") {
			return;
		}

		if (pendingNavigation.routeKey !== routeKey) {
			return;
		}

		let cancelled = false;

		const completeNavigation = async () => {
			await waitForNextFrame();
			await scrollToHashTarget(pendingNavigation.hash);

			if (cancelled) {
				return;
			}

			const previousUrl = new URL(
				latestHrefRef.current || window.location.href,
			);
			const currentUrl = new URL(window.location.href);

			await runEnter(
				previousUrl,
				currentUrl,
				pendingNavigation.mode,
				pendingNavigation.trigger,
			);

			latestHrefRef.current = currentUrl.href;
			pendingNavigationRef.current = null;
			completionResolverRef.current?.();
			completionResolverRef.current = null;
		};

		void completeNavigation();

		return () => {
			cancelled = true;
		};
	}, [routeKey, runEnter]);

	useEffect(() => {
		const handlePopState = () => {
			if (getBarbaIsRunning() || pendingNavigationRef.current) {
				return;
			}

			const previousUrl = new URL(
				latestHrefRef.current || window.location.href,
			);
			const nextUrl = new URL(window.location.href);
			const previousRouteKey = buildRouteKey(previousUrl);
			const nextRouteKey = buildRouteKey(nextUrl);

			if (previousRouteKey === nextRouteKey) {
				latestHrefRef.current = nextUrl.href;
				void scrollToHashTarget(nextUrl.hash);
				return;
			}

			pendingNavigationRef.current = {
				origin: getViewportOrigin(),
				hash: nextUrl.hash,
				mode: "route",
				routeKey: nextRouteKey,
				trigger: "popstate",
			};

			void runLeave(previousUrl, nextUrl, "route", "popstate");
		};

		window.addEventListener("popstate", handlePopState);

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [getBarbaIsRunning, runLeave]);

	const value = useMemo<PageTransitionContextValue>(
		() => ({
			isTransitioning,
			navigate,
		}),
		[isTransitioning, navigate],
	);

	return (
		<PageTransitionContext.Provider value={value}>
			<div
				ref={wrapperRef}
				className="relative min-h-screen"
				data-barba="wrapper"
			>
				<div
					ref={contentRef}
					className="min-h-screen"
					data-barba="container"
					data-barba-namespace={buildNamespace(
						new URL(`https://flowlab.local${routeKey}`),
					)}
				>
					{children}
				</div>

				<div
					ref={overlayRef}
					className="pointer-events-none fixed inset-0 z-120 opacity-0"
				>
					<div ref={panelRef} className="absolute inset-0 bg-gray-1000" />
				</div>
			</div>
		</PageTransitionContext.Provider>
	);
}

export function usePageTransition() {
	const context = useContext(PageTransitionContext);

	if (!context) {
		throw new Error(
			"usePageTransition must be used within PageTransitionProvider",
		);
	}

	return context;
}
